import { useEffect, useState, useCallback } from "react";
import { getMandateTable } from "../../Services/Mandate/MandateService";
import { getMandateById, } from "../../Services/Mandate/MandateService";
import { fetchProfile } from '../../Services/profile/profileservice';
import ViewMandateModal from "./ViewMandate";
import {
    showSuccessToast,
    showErrorToast,
} from "../../utils/toastUtils";
import useDebounce from "../../utils/useDebounce1";
import Loader from "../../Loader"

const MandateTable = ({ refreshKey, onSuccess, setGlobalLoading }) => {
    const [mandates, setMandates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [profile, setProfile] = useState({});
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [viewOpen, setViewOpen] = useState(false);
    const [selectedMandate, setSelectedMandate] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
    });
    const debouncedSearch = useDebounce(search, 500);

   const formatText = (text) =>
    text
        .replace(/_/g, " ")
        .toUpperCase();

    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                const response = await fetchProfile();
                setProfile(response.data);
            } catch (error) {
                console.error('Error fetching profile:', error);
            }
        };

        fetchProfileData();
    }, []);

    const handleViewMandate = async (mandateId) => {
        try {
            if (!profile) {
                showErrorToast("Profile not loaded yet");
                return;
            }

            const payload = {
                api_id: profile?.user_id,
                api_key: profile?.api_key,
                token_id: profile?.token_id,
                mandate_id: mandateId,
            };

            setViewLoading(true);

            const res = await getMandateById(payload);

            if (res?.status?.code !== 200) {
                showErrorToast(res?.data?.status?.message || "Failed to Fetch Mandates");
                return;
            }

            setSelectedMandate(res?.data);
            showSuccessToast("Mandate details fetched successfully");
            setViewOpen(true);
        } catch (error) {
            showErrorToast(
                error?.response?.data?.status?.message || "Failed to fetch mandate"
            );
        } finally {
            setViewLoading(false);
        }
    };

    const fetchMandates = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const res = await getMandateTable({
                page,
                per_page: perPage,
                search: debouncedSearch,
                status,
            });

            setMandates(res?.data || []);
            setPagination(res?.pagination || {
                current_page: 1,
                last_page: 1,
            });
        } catch (err) {
            setError("Failed to fetch mandates");
            setMandates([]);
        } finally {
            setLoading(false);
        }
    }, [page, perPage, debouncedSearch, status]);


    useEffect(() => {
        fetchMandates();
    }, [fetchMandates, refreshKey]);

    const getPaginationPages = (current, last) => {
        if (last <= 1) return [1];


        if (last <= 8) {
            return Array.from({ length: last }, (_, i) => i + 1);
        }

        const pages = [];

        pages.push(1, 2);

        if (current > 4) {
            pages.push("...");
        }

        if (current > 3 && current < last - 2) {
            pages.push(current - 1, current, current + 1);
        } else if (current <= 3) {
            pages.push(3, 4);
        } else {
            pages.push(last - 3, last - 2);
        }

        if (current < last - 3) {
            pages.push("...");
        }

        pages.push(last - 1, last);

        return [...new Set(pages)].filter(
            (p) => p === "..." || (p >= 1 && p <= last)
        );
    };

    return (
        <div className="bg-white rounded-xl shadow p-6">
            {/* HEADER */}
            <div className="mb-4">
                {/* Title */}
                <div className="w-full flex justify-center mb-3">
                    <h2 className="text-2xl font-semibold">
                        Mandates
                    </h2>
                </div>

                {/* SEARCH + FILTERS */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <input
                            type="text"
                            placeholder="Search by name"
                            value={search}
                            onChange={(e) => {
                                setPage(1);
                                setSearch(e.target.value);
                            }}
                            className="border rounded px-3 py-2 w-full sm:w-64"
                        />

                        <select
                            value={status}
                            onChange={(e) => {
                                setPage(1);
                                setStatus(e.target.value);
                            }}
                            className="border rounded px-3 py-2 w-full sm:w-auto"
                        >
                            <option value="">All Status</option>
                            <option value="PENDING">Pending</option>
                            <option value="ACTIVATED">Active</option>
                            <option value="FINISHED">Finished</option>
                            <option value="FAILED">Failed</option>
                            <option value="CREATED">Created</option>
                            <option value="CANCELLED">Cancelled</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-2 justify-end">
                        <span className="text-sm text-gray-600">
                            Rows per page:
                        </span>
                        <select
                            value={perPage}
                            onChange={(e) => {
                                setPage(1);
                                setPerPage(Number(e.target.value));
                            }}
                            className="border rounded px-2 py-1"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={15}>15</option>
                            <option value={25}>25</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* 🔹 TABLE */}
            <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-300 text-sm text-center divide-x divide-gray-300">

                    <thead className="bg-blue-700  text-white">
                        <tr>
                            <th className="px-4 py-2">Account Number</th>
                            <th className="px-4 py-2">Name</th>
                            <th className="px-4 py-2">Mobile</th>
                            <th className="px-4 py-2">Amount</th>
                            <th className="px-4 py-2">Frequency</th>
                            <th className="px-4 py-2">Status</th>
                            <th className="px-4 py-2">Description</th>
                            <th className="px-4 py-2">Created At</th>
                            <th className="px-4 py-2">Deleted</th>
                            <th className="px-4 py-2">View</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan="9" className="text-center py-6">
                                    Loading mandates...
                                </td>
                            </tr>
                        )}

                        {!loading && mandates.length === 0 && (
                            <tr>
                                <td colSpan="9" className="text-center py-6 text-gray-500">
                                    No mandates found
                                </td>
                            </tr>
                        )}

                        {!loading &&
                            mandates?.map((m) => (
                                <tr key={m.id} className="border-t">
                                    <td className="px-4 py-2">{m?.mandate_account_number}</td>
                                    <td className="px-4 py-2  border-l">
                                        <span
                                            title={m?.name || "N/A"}
                                            className="cursor-pointer"
                                        >
                                            {m?.name
                                                ? m?.name?.length > 16
                                                    ? `${m?.name.slice(0, 16)}...`
                                                    : m?.name
                                                : "N/A"}
                                        </span>
                                    </td>

                                    <td className="px-4 py-2 border-l">{m?.mobile_number}</td>
                                    <td className="px-4 py-2 border-l">₹ {m?.amount}</td>
                                    <td className="px-4 py-2 border-l">{m?.frequency}</td>
                                    <td className="px-4 py-2 text-center border-l">
                                        <span
                                            title={formatText(m?.status) || "N/A"}
                                            className="cursor-pointer"
                                        >
                                            {m?.status
                                                ? m.status.length > 12
                                                    ? `${formatText(m.status.slice(0, 12))}...`
                                                    : formatText(m.status)
                                                : "N/A"}
                                        </span>
                                    </td>

                                    <td className="px-4 py-2 text-center border-l">
                                        <span
                                            title={m?.description || "N/A"}
                                            className="cursor-pointer"
                                        >
                                            {m?.description
                                                ? m.description.length > 8
                                                    ? `${m.description.slice(0, 8)}...`
                                                    : m.description
                                                : "N/A"}
                                        </span>
                                    </td>

                                    <td className="px-4 py-2 border-l">
                                        {new Date(m?.created_at).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-2 border-l">
                                        {m?.is_deleted ? "Yes" : "No"}
                                    </td>
                                    <td className="px-4 py-2 border-l">
                                        <button
                                            disabled={!profile && viewLoading}
                                            onClick={() => handleViewMandate(m?.mandate_id)}
                                            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                    </tbody>

                </table>
            </div>

            {/* 🔹 PAGINATION */}
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <span className="text-sm text-gray-600 text-center sm:text-left">
                    Page {pagination?.current_page} of {pagination?.last_page}
                </span>

                <div className="flex flex-wrap gap-2 justify-center sm:justify-end">
                    <button
                        disabled={page === 1}
                        onClick={() => setPage((p) => p - 1)}
                        className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                        Prev
                    </button>

                    {getPaginationPages(page, pagination?.last_page).map(
                        (p, i) =>
                            p === "..." ? (
                                <span key={i} className="px-2 text-gray-500">
                                    ...
                                </span>
                            ) : (
                                <button
                                    key={i}
                                    onClick={() => setPage(p)}
                                    className={`px-3 py-1 border rounded ${page === p ? "bg-blue-600 text-white" : ""
                                        }`}
                                >
                                    {p}
                                </button>
                            )
                    )}

                    <button
                        disabled={page === pagination?.last_page}
                        onClick={() => setPage((p) => p + 1)}
                        className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            </div>
            <ViewMandateModal
                open={viewOpen}
                loading={viewLoading}
                data={selectedMandate}
                onSuccess={onSuccess}              
                setGlobalLoading={setGlobalLoading} 
                onClose={() => {
                    setViewOpen(false);
                    setSelectedMandate(null);
                }}

            />

            {viewLoading && <Loader />}

            {error && (
                <p className="text-red-600 text-center mt-4">{error}</p>
            )}
        </div>

    );
};

export default MandateTable;